import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cropdarpanLogo from '../assets/images/logo.png';
import cottonImage from '../assets/images/cotton.png';
import paddyImage from '../assets/images/paddy.png';
import chilliImage from '../assets/images/chilli.png';


const HomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedCrop, setSelectedCrop] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cropData = sessionStorage.getItem('selectedCrop');
    if (!cropData) {
      navigate('/');
      return;
    }
    setSelectedCrop(JSON.parse(cropData));
    setLoading(false);
  }, [navigate]);

  const handleFindDisease = () => {
    if (selectedCrop) {
      navigate(`/crops/${selectedCrop.cid}/symptom/level/1`);
    }
  };

  const handleFindAdvice = () => {
    if (selectedCrop) {
      navigate(`/crops/${selectedCrop.cid}/disease`);
    }
  };
  const handleBack = () => {
    // Keep the selected crop in sessionStorage when navigating back
    navigate('/');
  };
  const renderCropNameTranslation = (cropData) => {
    if (!cropData) return null;
    if(cropData.cid === 1) return t('cotton');
    else if(cropData.cid === 2) return t('paddy');
    else if(cropData.cid === 3) return t('chilli');
  };
  const renderCropIcon = (cropData) => {
    if (!cropData) return null;
    if (cropData.cid === 1) return cottonImage;
    else if (cropData.cid === 2) return paddyImage;
    else if (cropData.cid === 3) return chilliImage;
  };



  return (
    <div className="min-h-screen bg-white">
      <div className="flex flex-col items-center justify-center px-4 py-12">
        {/* Logo */}
        <div className="w-60 mb-6">
          <img src={cropdarpanLogo} alt="Crop Darpan Logo" className="w-full" />
        </div>

        {/* Card Container */}
        <div className="w-full max-w-md">
          <div className="bg-gray-100 border border-gray-300 rounded-lg p-6 shadow-md">            {/* Header */}
            <div className="mb-4 text-center">              
              <h2 className="text-2xl font-semibold text-gray-800">
                <span className="text-blue-600">{t('languages.selectCrop')}:</span>{' '}
                  {selectedCrop && (
                    <img
                      src={renderCropIcon(selectedCrop)}
                      alt="Crop Icon"
                      className="w-8 h-8 inline-block"
                    />
                  )}
                {renderCropNameTranslation(selectedCrop)}
              </h2>
            </div>

            <div className="text-sm text-blue-700 text-center mb-6 space-y-2">    
              <p>
                {t('home.developedBy')} &nbsp;
                <a
                  href={t('home.link1')}
                  className="underline hover:text-blue-900 transition-colors duration-200"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('home.line1')}
                </a>
              
                &nbsp;{t('and')} &nbsp;

                {selectedCrop && selectedCrop.cid === 3 ? (
                  <>
                    <a
                      href={t('home.chilli.link2')}
                      className="underline hover:text-blue-900 transition-colors duration-200"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('home.chilli.line2')}
                    </a>
                    &nbsp;{t('and')}&nbsp;
                    <a
                      href={t('home.chilli.link3')}
                      className="underline hover:text-blue-900 transition-colors duration-200"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('home.chilli.line3')}
                    </a>
                  </>
                ) : (
                  <a
                    href={t('home.link2')}
                    className="underline hover:text-blue-900 transition-colors duration-200"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('home.line2')}
                  </a>
                )}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-6">
              <div className="flex justify-between gap-4">
                {/* Find Disease */}
                  <button
                    onClick={() => setSelectedAction('disease')}
                    className={`w-full ${
                      selectedAction === 'disease' ? 'bg-green-600 text-white' : 'bg-gray-300 text-black hover:bg-green-800 hover:text-white'
                    } py-3 px-4 rounded-lg font-semibold transition-all duration-200`}
                  >
                    {t('home.findDiseaseButton')}
                  </button>

                {/* Find Advice */}
                  <button
                    onClick={() => setSelectedAction('advice')}
                    className={`w-full ${
                      selectedAction === 'advice' ? 'bg-blue-800 text-white' : 'bg-gray-300 text-black hover:bg-blue-900 hover:text-white'
                    } py-3 px-4 rounded-lg font-semibold transition-all duration-200`}
                  >
                    {t('home.findAdviceButton')}
                  </button>
              </div>

              {/* Proceed & Back Buttons */}
              <div className="flex justify-between gap-4 pt-4">
                <button
                  onClick={handleBack}
                  className="flex-1 border border-gray-300 bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 transition"
                >
                  {t('common.back')}
                </button>
                <button
                  onClick={() => {
                    if (selectedAction === 'disease') handleFindDisease();
                    else if (selectedAction === 'advice') handleFindAdvice();
                  }}
                  disabled={!selectedAction}
                  className={`flex-1 ${
                    selectedAction
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  } py-3 px-4 rounded-lg font-medium transition`}
                >
                  {t('common.proceed')}
                </button>
              </div>
            </div>

            {/* Bottom Nav */}
            <div className="flex justify-between gap-4 pt-6 mt-6 border-t">
              <button
                onClick={() => navigate('/about')}
                className="bg-blue-600 border border-gray-300 text-white py-2 px-4 rounded flex-1 text-center hover:bg-blue-700"
              >
                {t('home.aboutCropDarpan')}
              </button>
              <button
                onClick={() => navigate('/about-us')}
                className="bg-blue-600 border border-gray-300 text-white py-2 px-4 rounded flex-1 text-center hover:bg-blue-700"
              >
                {t('home.aboutUs')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
